"use client"

import { type ReactNode, useState } from "react"
import { cn } from "@/lib/utils"
import { CheckCircleIcon } from "@/components/ui/icons"

function InfoTooltip({ text, ariaLabel }: { text: string; ariaLabel: string }) {
  const [open, setOpen] = useState(false)
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        className="group/info ml-2 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        aria-label={ariaLabel}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle
            cx="11"
            cy="11"
            r="11"
            className="fill-neutral-100 transition-colors duration-200 group-hover/info:fill-neutral-200"
          />
          <path
            transform="translate(10.0672, 9.8516)"
            d="M1.7368 0.86837V5.78947C1.7368 6.26907 1.348 6.65787 0.8684 6.65787C0.3888 6.65787 0 6.26907 0 5.78947V0.86837C0 0.38877 0.3888 0 0.8684 0C1.348 0 1.7368 0.38877 1.7368 0.86837Z"
            className="fill-neutral-500 transition-colors duration-200 group-hover/info:fill-neutral-700"
          />
          <path
            transform="translate(9.999, 5.9994)"
            d="M1.8706 0.935C1.8706 1.19996 1.78 1.42209 1.5988 1.60137C1.4177 1.78065 1.1966 1.87029 0.9353 1.87029C0.674 1.87029 0.4528 1.78065 0.2718 1.60137C0.0906 1.42209 0 1.19996 0 0.935C0 0.67004 0.0896 0.44791 0.2689 0.26863C0.4482 0.08955 0.6703 0 0.9353 0C1.2003 0 1.4224 0.08955 1.6017 0.26863C1.7809 0.44791 1.8706 0.67004 1.8706 0.935Z"
            className="fill-neutral-500 transition-colors duration-200 group-hover/info:fill-neutral-700"
          />
        </svg>
      </button>
      {open && (
        <span className="absolute top-1/2 left-6 z-50 block w-64 -translate-y-1/2 rounded-xl border border-neutral-100 bg-white p-3 text-xs leading-relaxed text-neutral-600 shadow-lg">
          {text}
        </span>
      )}
    </span>
  )
}

export type ComparisonGridPackage = "core" | "advanced" | "pro"

const PACKAGE_LABELS: Record<ComparisonGridPackage, string> = {
  core: "Core",
  advanced: "Advanced",
  pro: "Pro",
}

export interface ComparisonGridRow {
  feature: string
  core: boolean
  advanced: boolean
  pro: boolean
  info?: string
}

export interface ComparisonGridProps {
  label: string
  /** Small icon rendered to the left of the label (e.g. a 32px circle) */
  icon?: ReactNode
  rows: ComparisonGridRow[]
  /**
   * @deprecated kept for API compatibility — the comparison grid now
   * always renders text-only column headers (no package icon badges).
   */
  showIcons?: boolean
  /** Aria-label for info tooltip buttons */
  moreInfoLabel?: string
  /** The tier columns to show, in order. Defaults to all three; a market that
   *  doesn't sell a tier (Austria: no Core) passes the ones it does. */
  packages?: ComparisonGridPackage[]
}

export function ComparisonGrid({
  label,
  icon,
  rows,
  moreInfoLabel = "More info",
  packages = ["core", "advanced", "pro"],
}: ComparisonGridProps) {
  // Static class names so Tailwind picks them up: feature column + one per tier.
  const gridCols = packages.length === 2 ? "grid-cols-3" : "grid-cols-4"
  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        {icon}
        <p className="type-label-lg text-neutral-400">{label}</p>
      </div>
      <div className={cn("mb-2 grid gap-2", gridCols)}>
        <div />
        {packages.map((pkg) => (
          <p key={pkg} className="type-title-lg text-center text-neutral-700">
            {PACKAGE_LABELS[pkg]}
          </p>
        ))}
      </div>
      {rows.map((row, i) => (
        <div
          key={row.feature}
          className={cn(
            "grid items-center gap-2 py-2",
            gridCols,
            i < rows.length - 1 && "border-b border-neutral-100"
          )}
        >
          <p className="type-body-lg flex items-center text-neutral-600">
            {row.feature}
            {row.info && (
              <InfoTooltip text={row.info} ariaLabel={moreInfoLabel} />
            )}
          </p>
          {packages.map((pkg) => (
            <div key={pkg} className="flex justify-center">
              {row[pkg] ? (
                <CheckCircleIcon size={16} />
              ) : (
                <span className="text-neutral-200">&mdash;</span>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
